'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';

interface Material {
  id: string;
  name: string;
  fullName: string;
  description: string;
  color: string;
  image: string;
  properties: {
    resistenza: number;
    rigidita: number;
    durabilita: number;
    finitura: number;
    prezzo: number;
  };
  applications: string[];
}

interface TechnologyCategory {
  id: string;
  name: string;
  fullName: string;
  icon: React.ReactNode;
  color: string;
  materials: Material[];
}

const technologies: TechnologyCategory[] = [
  {
    id: 'fdm',
    name: 'FDM',
    fullName: 'Fused Deposition Modeling',
    color: 'from-primary-500 to-primary-700',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 4v16M18 4v16M6 12h12M6 8h12M6 16h12" />
      </svg>
    ),
    materials: [
      {
        id: 'pla',
        name: 'PLA',
        fullName: 'Acido Polilattico',
        description: 'Il PLA è uno dei materiali più utilizzati nella stampa 3D FDM. Derivato da risorse rinnovabili come amido di mais, è biodegradabile e facile da stampare. Ideale per prototipi, modelli architettonici e oggetti decorativi. Offre un\'ottima finitura superficiale e una vasta gamma di colori disponibili.',
        color: 'from-green-400 to-green-600',
        image: '/images/coasters.webp',
        properties: {
          resistenza: 3,
          rigidita: 4,
          durabilita: 3,
          finitura: 5,
          prezzo: 5,
        },
        applications: ['Prototipi estetici', 'Modelli architettonici', 'Gadget', 'Oggetti decorativi'],
      },
      {
        id: 'petg',
        name: 'PETG',
        fullName: 'Polietilene Tereftalato Glicole',
        description: 'Il PETG combina le migliori caratteristiche di PLA e ABS. È resistente, leggermente flessibile e con ottima resistenza chimica e agli UV, perfetto per contenitori, componenti meccanici e applicazioni all\'aperto.',
        color: 'from-cyan-400 to-cyan-600',
        image: '/images/petg.webp',
        properties: {
          resistenza: 4,
          rigidita: 3,
          durabilita: 4,
          finitura: 4,
          prezzo: 4,
        },
        applications: ['Contenitori tecnici', 'Parti trasparenti', 'Componenti outdoor', 'Prototipi funzionali'],
      },
      {
        id: 'abs',
        name: 'ABS',
        fullName: 'Acrilonitrile Butadiene Stirene',
        description: 'L\'ABS è un polimero termoplastico riconosciuto per la sua leggerezza e resistenza all\'usura. Molto utilizzato nell\'industria automobilistica e nell\'elettronica di consumo. Resiste bene agli urti e alle alte temperature, rendendolo ideale per parti funzionali e prototipi meccanici.',
        color: 'from-orange-400 to-orange-600',
        image: '/images/abs.webp',
        properties: {
          resistenza: 4,
          rigidita: 4,
          durabilita: 4,
          finitura: 3,
          prezzo: 4,
        },
        applications: ['Parti meccaniche', 'Involucri elettronici', 'Automotive', 'Giocattoli'],
      },
      {
        id: 'pc',
        name: 'PC',
        fullName: 'Policarbonato',
        description: 'Il Policarbonato è un termoplastico ad alte prestazioni con eccezionale resistenza agli urti e trasparenza ottica. Resiste a temperature elevate e offre stabilità dimensionale. Ideale per parti industriali, dispositivi di sicurezza e componenti che richiedono massima robustezza.',
        color: 'from-gray-400 to-gray-600',
        image: '/images/pc.webp',
        properties: {
          resistenza: 5,
          rigidita: 4,
          durabilita: 5,
          finitura: 3,
          prezzo: 3,
        },
        applications: ['Parti industriali', 'Dispositivi di sicurezza', 'Componenti trasparenti', 'Automotive'],
      },
      {
        id: 'nylon-12',
        name: 'Nylon 12',
        fullName: 'Poliammide 12 (FDM)',
        description: 'Il Nylon 12 stampato in FDM offre eccellente resistenza meccanica, flessibilità e resistenza chimica. È ideale per parti funzionali, prototipi resistenti e componenti industriali che richiedono durabilità e leggerezza.',
        color: 'from-indigo-400 to-indigo-600',
        image: '/images/nylon12.webp',
        properties: {
          resistenza: 5,
          rigidita: 3,
          durabilita: 5,
          finitura: 3,
          prezzo: 2,
        },
        applications: ['Parti funzionali', 'Componenti industriali', 'Ingranaggi', 'Prototipi meccanici'],
      },
      {
        id: 'tpu-fdm',
        name: 'TPU',
        fullName: 'Poliuretano Termoplastico (FDM)',
        description: 'Il TPU stampato in FDM è un elastomero flessibile che combina elasticità e resistenza all\'abrasione. Perfetto per guarnizioni, coperture protettive, suole e qualsiasi applicazione che richieda flessibilità e assorbimento degli urti.',
        color: 'from-rose-400 to-rose-600',
        image: '/images/tpu.webp',
        properties: {
          resistenza: 4,
          rigidita: 1,
          durabilita: 5,
          finitura: 3,
          prezzo: 3,
        },
        applications: ['Guarnizioni', 'Coperture protettive', 'Parti elastiche', 'Ammortizzatori'],
      },
      {
        id: 'onyx',
        name: 'Onyx',
        fullName: 'Nylon caricato con microfibre di carbonio',
        description: 'L\'Onyx è un nylon rinforzato con microfibre di carbonio che offre eccezionale rigidità, resistenza termica e una finitura superficiale di alta qualità. Ideale per parti ad alte prestazioni, attrezzature e componenti strutturali.',
        color: 'from-zinc-600 to-zinc-800',
        image: '/images/onyx.webp',
        properties: {
          resistenza: 5,
          rigidita: 5,
          durabilita: 5,
          finitura: 4,
          prezzo: 1,
        },
        applications: ['Parti strutturali', 'Attrezzature', 'Componenti ad alte prestazioni', 'Automotive'],
      },
    ],
  },
  {
    id: 'sla',
    name: 'SLA',
    fullName: 'Stereolitografia',
    color: 'from-violet-500 to-purple-700',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    materials: [
      {
        id: 'resina-standard',
        name: 'Resina Standard',
        fullName: 'Resina Fotopolimerica Standard',
        description: 'La resina standard è perfetta per prototipi dettagliati e modelli estetici. Offre un\'eccellente finitura superficiale e permette di catturare dettagli molto fini. Ideale per gioielleria, miniature, modelli dentali e prototipi di design.',
        color: 'from-violet-400 to-violet-600',
        image: '/images/resinastandard.webp',
        properties: {
          resistenza: 2,
          rigidita: 4,
          durabilita: 2,
          finitura: 5,
          prezzo: 3,
        },
        applications: ['Gioielleria', 'Miniature', 'Modelli dentali', 'Prototipi estetici'],
      },
      {
        id: 'resina-abs-like',
        name: 'Resina ABS-like',
        fullName: 'Resina Fotopolimerica ABS-like',
        description: 'La resina ABS-like simula le proprietà meccaniche dell\'ABS tradizionale. Offre maggiore resistenza agli urti e flessibilità rispetto alla resina standard, mantenendo un\'ottima risoluzione dei dettagli. Perfetta per prototipi funzionali.',
        color: 'from-amber-400 to-amber-600',
        image: '/images/resinaabs.webp',
        properties: {
          resistenza: 4,
          rigidita: 3,
          durabilita: 4,
          finitura: 5,
          prezzo: 2,
        },
        applications: ['Prototipi funzionali', 'Snap-fit', 'Parti meccaniche', 'Testing'],
      },
      {
        id: 'resina-tough',
        name: 'Resina Tough',
        fullName: 'Resina Fotopolimerica Tough',
        description: 'La resina Tough è progettata per resistere a stress e carichi elevati, simulando le proprietà meccaniche dell\'ABS. Offre eccellente resistenza agli urti e alla deformazione, mantenendo una buona finitura superficiale. Perfetta per prototipi funzionali e test meccanici.',
        color: 'from-emerald-500 to-emerald-700',
        image: '/images/resinatough.webp',
        properties: {
          resistenza: 5,
          rigidita: 3,
          durabilita: 4,
          finitura: 4,
          prezzo: 2,
        },
        applications: ['Prototipi funzionali', 'Test meccanici', 'Parti resistenti', 'Snap-fit'],
      },
    ],
  },
  {
    id: 'sls',
    name: 'SLS',
    fullName: 'Sinterizzazione Laser Selettiva',
    color: 'from-rose-500 to-red-700',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
      </svg>
    ),
    materials: [
      {
        id: 'nylon-pa12',
        name: 'Nylon PA12',
        fullName: 'Poliammide 12',
        description: 'Il Nylon PA12 è il materiale più diffuso nella stampa SLS. Offre eccellente resistenza meccanica, flessibilità e durabilità. Ideale per parti funzionali, prototipi meccanici e produzioni in serie. Resistente a urti, abrasione e molti agenti chimici.',
        color: 'from-blue-400 to-blue-600',
        image: '/images/pa12.webp',
        properties: {
          resistenza: 5,
          rigidita: 4,
          durabilita: 5,
          finitura: 3,
          prezzo: 2,
        },
        applications: ['Parti funzionali', 'Ingranaggi', 'Componenti industriali', 'Prototipi meccanici'],
      },
      {
        id: 'nylon-pa11',
        name: 'Nylon PA11',
        fullName: 'Poliammide 11',
        description: 'Il Nylon PA11 è derivato da fonti rinnovabili (olio di ricino) ed è più flessibile del PA12. Eccellente resistenza agli urti e all\'usura, ideale per applicazioni che richiedono maggiore elasticità. Ottima scelta per parti che devono resistere a stress ripetuti.',
        color: 'from-teal-400 to-teal-600',
        image: '/images/pa11.webp',
        properties: {
          resistenza: 4,
          rigidita: 3,
          durabilita: 5,
          finitura: 3,
          prezzo: 2,
        },
        applications: ['Parti flessibili', 'Snap-fit', 'Automotive', 'Sport e outdoor'],
      },
      {
        id: 'tpu-95a',
        name: 'TPU 95A',
        fullName: 'Poliuretano Termoplastico 95A',
        description: 'Il TPU 95A è un elastomero flessibile con durezza Shore 95A. Combina la flessibilità della gomma con la resistenza dei materiali tecnici. Perfetto per guarnizioni, ammortizzatori, calzature e qualsiasi applicazione che richieda elasticità e resistenza all\'abrasione.',
        color: 'from-pink-400 to-pink-600',
        image: '/images/tpusls.webp',
        properties: {
          resistenza: 4,
          rigidita: 1,
          durabilita: 5,
          finitura: 3,
          prezzo: 2,
        },
        applications: ['Guarnizioni', 'Ammortizzatori', 'Calzature', 'Parti elastiche'],
      },
    ],
  },
];

// Componente per i pallini di rating
function RatingDots({ value, max = 5, label }: { value: number; max?: number; label: string }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-secondary-100 last:border-0">
      <span className="text-sm text-secondary-400">{label}</span>
      <div className="flex gap-1.5">
        {Array.from({ length: max }).map((_, i) => (
          <div
            key={i}
            className={`w-3 h-3 rounded-full transition-all ${
              i < value
                ? 'bg-primary-500 shadow-sm shadow-primary-500/50'
                : 'bg-secondary-200'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Materials() {
  const [expandedTech, setExpandedTech] = useState<string>('fdm');
  const [selectedMaterial, setSelectedMaterial] = useState<Material>(technologies[0].materials[0]);

  const handleTechToggle = (techId: string) => {
    if (expandedTech === techId) {
      setExpandedTech('');
    } else {
      setExpandedTech(techId);
    }
  };

  const handleMaterialSelect = (material: Material) => {
    setSelectedMaterial(material);
  };

  return (
    <section id="materiali" className="py-24 bg-gradient-to-b from-white to-secondary-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="section-title font-display">
            Alcuni materiali per la <span className="text-gradient">stampa 3D</span>
          </h2>
          <p className="section-subtitle mx-auto">
            Offriamo una vasta gamma di materiali per soddisfare ogni esigenza produttiva.
          </p>
        </div>

        {/* Main card */}
        <div className="bg-white rounded-3xl shadow-2xl shadow-secondary-500/10 border border-secondary-100 overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Left sidebar - Material selector with collapsible categories */}
            <div className="lg:w-1/4 bg-secondary-50/50 border-b lg:border-b-0 lg:border-r border-secondary-100">
              <div className="p-4 lg:p-6">
                <h3 className="text-xs font-semibold text-secondary-400 uppercase tracking-wider mb-4">
                  Seleziona tecnologia e materiale
                </h3>
                <div className="space-y-2">
                  {technologies.map((tech) => (
                    <div key={tech.id} className="rounded-xl overflow-hidden">
                      {/* Technology header - collapsible */}
                      <button
                        onClick={() => handleTechToggle(tech.id)}
                        className={`w-full flex items-center justify-between gap-3 px-4 py-3 transition-all ${
                          expandedTech === tech.id
                            ? `bg-gradient-to-r ${tech.color} text-white shadow-lg`
                            : 'bg-white hover:bg-secondary-100 text-secondary-500 border border-secondary-100'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            expandedTech === tech.id 
                              ? 'bg-white/20' 
                              : `bg-gradient-to-br ${tech.color} text-white`
                          }`}>
                            {tech.icon}
                          </div>
                          <div className="text-left">
                            <span className="font-semibold text-sm block">{tech.name}</span>
                            <span className={`text-xs ${expandedTech === tech.id ? 'text-white/70' : 'text-secondary-400'}`}>
                              {tech.fullName}
                            </span>
                          </div>
                        </div>
                        <svg 
                          className={`w-5 h-5 transition-transform duration-200 ${
                            expandedTech === tech.id ? 'rotate-180' : ''
                          }`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {/* Materials list - collapsible */}
                      <div className={`overflow-hidden transition-all duration-300 ${
                        expandedTech === tech.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      }`}>
                        <div className="bg-white border-x border-b border-secondary-100 rounded-b-xl p-2 space-y-1">
                          {tech.materials.map((material) => (
                            <button
                              key={material.id}
                              onClick={() => handleMaterialSelect(material)}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left ${
                                selectedMaterial.id === material.id
                                  ? 'bg-primary-50 text-primary-600 border border-primary-200'
                                  : 'hover:bg-secondary-50 text-secondary-500'
                              }`}
                            >
                              <div
                                className={`w-6 h-6 rounded-md bg-gradient-to-br ${material.color} flex items-center justify-center shadow-sm flex-shrink-0`}
                              >
                                <span className="text-white font-bold text-xs">
                                  {material.name.charAt(0)}
                                </span>
                              </div>
                              <span className="font-medium text-sm">{material.name}</span>
                              {selectedMaterial.id === material.id && (
                                <svg className="w-4 h-4 ml-auto text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right content - Material details */}
            <div className="lg:w-3/4 p-6 lg:p-10">
              <div className="grid lg:grid-cols-2 gap-8">
                {/* Left column - Image & Applications */}
                <div>
                  {/* Material image */}
                  <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-secondary-100 to-secondary-200 mb-6">
                    {!selectedMaterial.image.includes('/materials/') ? (
                      <Image
                        src={selectedMaterial.image}
                        alt={selectedMaterial.name}
                        fill
                        className="object-cover"
                      />
                    ) : (
                      <>
                        <div className={`absolute inset-0 bg-gradient-to-br ${selectedMaterial.color} opacity-20`} />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className={`w-32 h-32 rounded-2xl bg-gradient-to-br ${selectedMaterial.color} flex items-center justify-center shadow-2xl`}>
                            <span className="text-white font-bold text-5xl">
                              {selectedMaterial.name.charAt(0)}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                    {/* Badge */}
                    <div className="absolute top-4 left-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${selectedMaterial.color} text-white shadow-lg`}>
                        {selectedMaterial.name}
                      </span>
                    </div>
                  </div>

                  {/* Applications */}
                  <div>
                    <h4 className="text-sm font-semibold text-secondary-400 uppercase tracking-wider mb-3">
                      Applicazioni
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedMaterial.applications.map((app) => (
                        <span
                          key={app}
                          className="inline-flex items-center px-3 py-1.5 rounded-lg bg-secondary-100 text-secondary-500 text-sm"
                        >
                          {app}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right column - Info & Properties */}
                <div>
                  {/* Header */}
                  <div className="mb-6">
                    <h3 className="text-2xl lg:text-3xl font-bold text-secondary-500 mb-1">
                      {selectedMaterial.name}
                    </h3>
                    <p className="text-sm text-secondary-400">
                      {selectedMaterial.fullName}
                    </p>
                  </div>

                  {/* Description */}
                  <p className="text-secondary-400 leading-relaxed mb-8">
                    {selectedMaterial.description}
                  </p>

                  {/* Properties with dot ratings */}
                  <div className="bg-secondary-50 rounded-xl p-5">
                    <h4 className="text-sm font-semibold text-secondary-400 uppercase tracking-wider mb-4">
                      Caratteristiche
                    </h4>
                    <div className="space-y-1">
                      <RatingDots value={selectedMaterial.properties.resistenza} label="Resistenza" />
                      <RatingDots value={selectedMaterial.properties.rigidita} label="Rigidità" />
                      <RatingDots value={selectedMaterial.properties.durabilita} label="Durabilità" />
                      <RatingDots value={selectedMaterial.properties.finitura} label="Finitura" />
                      <RatingDots value={selectedMaterial.properties.prezzo} label="Economicità" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Link href="/materiali" className="btn-outline">
            Vedi tutti i materiali
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}
